// Vanuatu National Syllabus: Chemistry - Senior Secondary (Years 11-13)
// St Patrick's College

export const chemistryCurriculum = {
  "Year 11": {
    courseInfo: {
      subject: "Chemistry",
      level: "Year 11",
      school: "St Patrick's College",
      assessmentWeighting: {
        internalAssessment: 40,
        externalExamination: 60
      }
    },
    strands: [
      {
        id: "y11-chem-1",
        name: "Atomic Structure, Bonding and Related Properties",
        weighting: 13,
        outcomes: [
          "Sketch and label the structure of the atom",
          "Define mass number, isotope, atomic number, and electronic configuration",
          "Describe the Octet rule and its use in determining electronic configuration",
          "Write electronic configuration of the first 20 elements",
          "Use the Periodic Table to locate elements and describe trends",
          "Draw Lewis structures of atoms",
          "Define and differentiate electrovalent (ionic) and covalent bonding",
          "Show the formation of ionic and covalent bonds using Lewis structures",
          "State and list properties of ionic and covalent compounds",
          "Write chemical formulas and balanced equations for various reactions",
          "Define inorganic compounds and describe their properties",
          "Use activity series to predict reactivity",
          "Use solubility rules to predict precipitation"
        ],
        topics: [
          { name: "Atomic Structure", weeks: "1-2", description: "Protons, neutrons, electrons, mass number, atomic number" },
          { name: "Electronic Configuration", weeks: "3-4", description: "Electron shells, Octet rule, first 20 elements" },
          { name: "Periodic Table Trends", weeks: "5-6", description: "Groups, periods, atomic size trends" },
          { name: "Chemical Bonding", weeks: "7-9", description: "Ionic and covalent bonds, Lewis structures" },
          { name: "Properties of Compounds", weeks: "10-11", description: "Ionic vs covalent properties, activity series" }
        ]
      },
      {
        id: "y11-chem-2",
        name: "Quantitative and Aqueous Chemistry",
        weighting: 15,
        outcomes: [
          "Describe the quantity of matter and the mole concept",
          "Define molar mass, mass, and Avogadro's number",
          "Calculate moles using various formulas",
          "Calculate percentage composition and determine empirical/molecular formulas",
          "Define stoichiometry and determine mole ratios",
          "Draw the structure of water and identify its polarity",
          "Explain the properties of water in relation to its bonds",
          "Describe solubility of substances and concentration of solutions",
          "Calculate concentration of solutions",
          "Describe and compare acidic and basic solutions using pH values",
          "Demonstrate understanding of practical investigation procedures"
        ],
        topics: [
          { name: "The Mole Concept", weeks: "1-3", description: "Molar mass, Avogadro's number, mole calculations" },
          { name: "Chemical Formulas", weeks: "4-5", description: "Empirical and molecular formulas, percentage composition" },
          { name: "Stoichiometry", weeks: "6-7", description: "Mole ratios, balanced equations, limiting reactants" },
          { name: "Water and Solutions", weeks: "8-9", description: "Water structure, polarity, solubility" },
          { name: "Acids and Bases Introduction", weeks: "10-11", description: "pH scale, acidic and basic solutions" }
        ]
      },
      {
        id: "y11-chem-3",
        name: "Physical Chemistry",
        weighting: 12,
        outcomes: [
          "Describe the law of conservation of energy",
          "Define potential and kinetic energy with examples",
          "Relate energy transfer to work",
          "Describe the composition of the atmosphere and greenhouse gases",
          "Describe the protective ability of ozone",
          "Write word equations for combustion reactions",
          "Discuss effects of carbon dioxide on coral and environment",
          "Describe Collision Theory",
          "Explain effect of temperature, pressure, volume, and surface area on reaction rates",
          "Define Activation Energy",
          "Define open and closed systems, and reversible reactions",
          "Define dynamic equilibrium"
        ],
        topics: [
          { name: "Energy in Chemistry", weeks: "1-2", description: "Conservation of energy, potential and kinetic energy" },
          { name: "Atmospheric Chemistry", weeks: "3-4", description: "Greenhouse gases, ozone layer, combustion" },
          { name: "Collision Theory", weeks: "5-7", description: "Factors affecting reaction rates, activation energy" },
          { name: "Chemical Equilibrium", weeks: "8-10", description: "Reversible reactions, dynamic equilibrium" }
        ]
      },
      {
        id: "y11-chem-4",
        name: "Oxidation and Reduction",
        weighting: 10,
        outcomes: [
          "Define oxidation and reduction in terms of hydrogen, oxygen, and electrons",
          "Describe and write ionic equations for oxidation-reduction reactions",
          "Identify single displacement, double displacement, combustion, and corrosion reactions",
          "Balance simple redox equations"
        ],
        topics: [
          { name: "Introduction to Redox", weeks: "1-2", description: "Oxidation and reduction definitions" },
          { name: "Types of Redox Reactions", weeks: "3-4", description: "Displacement, combustion, corrosion" },
          { name: "Balancing Redox Equations", weeks: "5-6", description: "Writing ionic equations, balancing" }
        ]
      },
      {
        id: "y11-chem-5",
        name: "Organic Chemistry",
        weighting: 10,
        outcomes: [
          "Define hydrocarbons and saturated/unsaturated compounds",
          "Use general formulas to determine molecular formulas of alkanes, alkenes, and alkynes",
          "Draw structural formulas and determine structural/geometrical isomers",
          "Use IUPAC nomenclature rules to name hydrocarbons",
          "Give examples of uses of alkenes and describe environmental problems",
          "Use tests (bromine water, KMnO4) to distinguish between saturated and unsaturated compounds",
          "Identify and research issues related to chemistry in the community"
        ],
        topics: [
          { name: "Introduction to Hydrocarbons", weeks: "1-2", description: "Saturated vs unsaturated, alkanes" },
          { name: "Alkenes and Alkynes", weeks: "3-4", description: "Double and triple bonds, general formulas" },
          { name: "Isomerism", weeks: "5-6", description: "Structural and geometrical isomers" },
          { name: "IUPAC Nomenclature", weeks: "7-8", description: "Naming hydrocarbons systematically" },
          { name: "Chemical Tests", weeks: "9-10", description: "Bromine water test, KMnO4 test" }
        ]
      }
    ]
  },

  "Year 12": {
    courseInfo: {
      subject: "Chemistry",
      level: "Year 12",
      school: "St Patrick's College",
      assessmentWeighting: {
        internalAssessment: 40,
        externalExamination: 60
      }
    },
    strands: [
      {
        id: "y12-chem-1",
        name: "Atomic Structure, Bonding and Related Properties",
        outcomes: [
          "Write formulas for ions and their compounds",
          "Write electronic configurations for ions",
          "Use the Periodic Table to determine ion charges and trends",
          "Calculate % abundance of isotopes and atomic mass",
          "Define ionic size, ionization energy, and electronegativity",
          "Describe properties and models of covalent networks and molecules",
          "Explain intermolecular forces",
          "Explain the ratio of atoms in oxides and chlorides",
          "Relate ionic and covalent oxides to periodic table positions",
          "Describe trends from basic to acidic oxides",
          "Write balanced equations for oxide properties",
          "Describe solubilities of various salts",
          "Name complex ions and describe their colors"
        ],
        topics: [
          { name: "Ionic Compounds", weeks: "1-2", description: "Ion formation, ionic formulas, electronic configurations" },
          { name: "Periodic Trends", weeks: "3-4", description: "Ionization energy, electronegativity, atomic/ionic radii" },
          { name: "Isotopes", weeks: "5", description: "Isotope calculations, atomic mass" },
          { name: "Covalent Structures", weeks: "6-7", description: "Covalent networks, intermolecular forces" },
          { name: "Oxides and Chlorides", weeks: "8-9", description: "Acidic, basic, amphoteric oxides" },
          { name: "Complex Ions", weeks: "10-11", description: "Naming, colors, solubility" }
        ]
      },
      {
        id: "y12-chem-2",
        name: "Quantitative and Aqueous Chemistry",
        outcomes: [
          "Calculate mole using various formulas",
          "Determine mole ratios, limiting reactants, and maximum product amounts",
          "Calculate percentage yield",
          "Define and list features of primary and standard solutions",
          "Prepare standard solutions and report on the process",
          "Calculate concentrations of solutions",
          "Explain acid-base titrations and define related terms",
          "Carry out titrations and report findings",
          "Describe indicator color changes",
          "Calculate unknown concentrations using titration data",
          "Define acid and base using different models",
          "Describe properties and reactions of acids and bases",
          "Define conjugate acid and base",
          "Describe water as an amphoteric substance",
          "Define ionic product of water (Kw)",
          "Calculate pH and pOH"
        ],
        topics: [
          { name: "Advanced Stoichiometry", weeks: "1-3", description: "Limiting reactants, percentage yield" },
          { name: "Standard Solutions", weeks: "4-5", description: "Primary standards, preparation, calculations" },
          { name: "Titrations", weeks: "6-8", description: "Acid-base titrations, indicators, calculations" },
          { name: "Acid-Base Theory", weeks: "9-10", description: "Arrhenius, Bronsted-Lowry models, conjugate pairs" },
          { name: "pH Calculations", weeks: "11-12", description: "Kw, pH, pOH calculations" }
        ]
      },
      {
        id: "y12-chem-3",
        name: "Physical Chemistry",
        outcomes: [
          "Define exothermic and endothermic reactions",
          "Define enthalpy of reaction (ΔH) and activation energy",
          "Relate energy change to thermochemical equations",
          "Sketch and interpret energy profile diagrams",
          "Define and apply Hess's Law to calculate enthalpy changes",
          "Classify reactions using ΔH",
          "List factors affecting reaction rates and explain their effects",
          "Define catalysts and discuss their effects",
          "Explain dynamic equilibrium in reversible systems",
          "Define and apply Le Chatelier's Principle",
          "Explain effects of temperature, pressure, volume, and concentration on equilibrium",
          "Explain applications in industrial processes (Contact and Haber)"
        ],
        topics: [
          { name: "Thermochemistry", weeks: "1-3", description: "Enthalpy, exothermic/endothermic reactions" },
          { name: "Energy Diagrams", weeks: "4-5", description: "Energy profiles, activation energy" },
          { name: "Hess's Law", weeks: "6-7", description: "Enthalpy calculations using Hess's Law" },
          { name: "Reaction Rates", weeks: "8-9", description: "Factors affecting rates, catalysts" },
          { name: "Chemical Equilibrium", weeks: "10-12", description: "Le Chatelier's Principle, industrial applications" }
        ]
      },
      {
        id: "y12-chem-4",
        name: "Oxidation and Reduction",
        outcomes: [
          "Determine oxidation states of atoms",
          "Explain oxidation and reduction in terms of oxidation states",
          "Define oxidant and reductant and identify common examples",
          "Balance redox reactions using the ion-electron method",
          "Define electrolytes and electrolysis",
          "Draw and label an electrolytic cell",
          "Describe observations at electrodes during electrolysis",
          "Write anode and cathode reactions for electrolysis",
          "Identify examples of oxidation and reduction in the environment",
          "Explain the impact of redox reactions on people and environment"
        ],
        topics: [
          { name: "Oxidation States", weeks: "1-2", description: "Assigning oxidation states, identifying redox" },
          { name: "Balancing Redox Equations", weeks: "3-4", description: "Ion-electron method, half-reactions" },
          { name: "Electrolysis", weeks: "5-7", description: "Electrolytic cells, electrode reactions" },
          { name: "Redox in Environment", weeks: "8-9", description: "Corrosion, environmental impact" }
        ]
      },
      {
        id: "y12-chem-5",
        name: "Organic Chemistry",
        outcomes: [
          "Use general formulas for alcohols, aldehydes, and ketones",
          "Identify functional groups of alcohols, aldehydes, and ketones",
          "Draw structural formulas and determine isomers",
          "Use IUPAC nomenclature to name organic compounds",
          "Identify types of alcohols (primary, secondary, tertiary)",
          "Deduce products of oxidation of alcohols",
          "Carry out tests for ketones and aldehydes",
          "Identify functional groups of carboxylic acids and esters",
          "Describe acidic behavior of carboxylic acids",
          "Write equations for esterification reactions",
          "Carry out laboratory preparation of esters",
          "State physical properties of esters",
          "Describe structure of fats and oils as esters",
          "Draw structures of triglycerides",
          "Relate melting points to saturation in fats/oils",
          "Describe extraction and hydrogenation of fats/oils",
          "Describe chemical properties and production of soap",
          "Explain the cleaning action of soap"
        ],
        topics: [
          { name: "Alcohols", weeks: "1-3", description: "Structure, naming, types, oxidation" },
          { name: "Aldehydes and Ketones", weeks: "4-5", description: "Functional groups, tests, naming" },
          { name: "Carboxylic Acids", weeks: "6-7", description: "Acidic properties, reactions" },
          { name: "Esters", weeks: "8-9", description: "Esterification, properties, naming" },
          { name: "Fats, Oils and Soaps", weeks: "10-12", description: "Triglycerides, hydrogenation, saponification" }
        ]
      }
    ]
  },

  "Year 13": {
    courseInfo: {
      subject: "Chemistry",
      level: "Year 13",
      school: "St Patrick's College",
      assessmentWeighting: {
        internalAssessment: 40,
        externalExamination: 60
      }
    },
    strands: [
      {
        id: "y13-chem-1",
        name: "Atomic Structure, Bonding and Related Properties",
        outcomes: [
          "Describe features and examples of atomic spectra",
          "Explain the relationship between atomic spectra and electron behavior",
          "Describe the Bohr model of the Hydrogen atom",
          "Relate wave properties and mechanics to electron positions using quantum numbers",
          "Write ground state electronic configurations",
          "Define electron affinity",
          "Discuss effect of electron-shielding on atomic and ionic radii",
          "Draw Lewis structures",
          "State trends in atomic and ionic radii, ionization energy, electronegativity",
          "Discuss molecular shapes using VSEPR theory",
          "Determine polarity of molecules and deduce intermolecular forces",
          "Define fission, fusion, and radioactivity",
          "Identify alpha, beta, and gamma emissions",
          "Describe nuclear transformations using equations",
          "Perform calculations involving radioactive decay and half-life",
          "Compare beneficial and detrimental effects of radioactivity",
          "Identify transition metals and their characteristic properties",
          "Write electronic configurations of transition metals",
          "Name and write equations for formation of complex ions"
        ],
        topics: [
          { name: "Atomic Spectra", weeks: "1-2", description: "Emission spectra, Bohr model" },
          { name: "Quantum Numbers", weeks: "3-4", description: "Wave mechanics, electron configurations" },
          { name: "Periodic Trends", weeks: "5-6", description: "Electron shielding, ionization energy, electron affinity" },
          { name: "Molecular Shapes", weeks: "7-8", description: "VSEPR theory, polarity, intermolecular forces" },
          { name: "Nuclear Chemistry", weeks: "9-11", description: "Radioactivity, fission, fusion, half-life" },
          { name: "Transition Metals", weeks: "12-13", description: "Properties, complex ions, electronic structure" }
        ]
      },
      {
        id: "y13-chem-2",
        name: "Quantitative and Aqueous Chemistry",
        outcomes: [
          "Define ionisation/dissociation constants (Ka, Kb)",
          "Relate Ka to acid strength",
          "Calculate hydrogen/hydroxide ion concentrations",
          "Calculate pH and pOH from Ka and Kb",
          "Define buffer and describe buffer actions",
          "Describe resistance to pH changes in buffer solutions",
          "Calculate pH/pOH of buffer solutions",
          "Draw and interpret titration curves",
          "Define equivalence point and identify it on titration curves",
          "Propose appropriate indicators for titrations",
          "Identify buffer regions on titration curves"
        ],
        topics: [
          { name: "Acid-Base Equilibria", weeks: "1-3", description: "Ka, Kb, weak acid/base calculations" },
          { name: "Buffer Solutions", weeks: "4-6", description: "Buffer action, pH calculations" },
          { name: "Titration Curves", weeks: "7-9", description: "Strong/weak acid-base curves, indicators" }
        ]
      },
      {
        id: "y13-chem-3",
        name: "Physical Chemistry",
        outcomes: [
          "Define Hess's law and use it to calculate enthalpy changes",
          "Define bond energy and explain bond making/breaking",
          "Use energy diagrams to calculate average bond energy",
          "Define standard heats of formation, combustion, reaction, vaporization, fusion",
          "Calculate enthalpy changes using various methods",
          "Use specific heat capacity for enthalpy calculations",
          "Describe the First Law of Thermodynamics",
          "Define reaction quotient (Qc) and equilibrium constant (Kc)",
          "Write reaction quotient expressions",
          "Relate Qc to Kc and apply to real-life situations",
          "Calculate Kc values",
          "Relate Kc to degree of completion of reactions",
          "Define solubility product (Ksp)",
          "Write solubility product expressions",
          "Calculate Ksp values",
          "Determine common ion effect"
        ],
        topics: [
          { name: "Advanced Thermochemistry", weeks: "1-3", description: "Bond energy, Hess's Law calculations" },
          { name: "Standard Enthalpies", weeks: "4-5", description: "Formation, combustion, reaction enthalpies" },
          { name: "Thermodynamics", weeks: "6-7", description: "First Law, heat capacity calculations" },
          { name: "Equilibrium Constants", weeks: "8-10", description: "Kc calculations, reaction quotient" },
          { name: "Solubility Equilibria", weeks: "11-12", description: "Ksp calculations, common ion effect" }
        ]
      },
      {
        id: "y13-chem-4",
        name: "Oxidation and Reduction",
        outcomes: [
          "State features of oxidation-reduction reactions",
          "Write and balance redox equations",
          "Describe redox titrations",
          "Discuss redox titration results and errors",
          "Define galvanic (voltaic) cells",
          "Identify features of galvanic cells",
          "Describe how electrochemical cells work",
          "Explain electrochemical cells in terms of components and reactions",
          "Compare electrochemical cells",
          "Represent electrochemical cells using IUPAC notation",
          "Calculate E° for electrochemical cells",
          "Determine spontaneity of reactions",
          "Use E° values to compare oxidant and reductant strengths",
          "Describe differences between electrochemical and electrolytic cells"
        ],
        topics: [
          { name: "Redox Titrations", weeks: "1-2", description: "Titration procedures, calculations, errors" },
          { name: "Galvanic Cells", weeks: "3-5", description: "Cell construction, half-cells, salt bridge" },
          { name: "Electrode Potentials", weeks: "6-8", description: "E° values, cell potential calculations" },
          { name: "Electrochemical vs Electrolytic", weeks: "9-10", description: "Comparing cell types, applications" }
        ]
      },
      {
        id: "y13-chem-5",
        name: "Organic Chemistry",
        outcomes: [
          "Identify functional groups for multiple compound classes",
          "Draw structures and isomers of organic compounds",
          "Use IUPAC rules to name isomers",
          "Differentiate between primary, secondary, and tertiary amines",
          "Describe oxidation and reduction reactions of organic compounds",
          "Describe substitution, elimination, and hydrolysis reactions",
          "Discuss applications of organic reactions in real life",
          "Distinguish between aldehydes and ketones using specific reagents",
          "Describe formation of esters, acyl chlorides, and amides",
          "Describe reactions of acid chlorides",
          "Illustrate formation of alcohols and carboxylic acids from ester hydrolysis",
          "Describe formation of fats and oils",
          "Explain properties of fats and oils",
          "Describe saponification process",
          "Describe formation of carbohydrates",
          "Deduce differences between mono-, di-, and polysaccharides",
          "Draw structures of carbohydrates",
          "Discuss applications of carbohydrates"
        ],
        topics: [
          { name: "Amines and Amides", weeks: "1-2", description: "Structure, naming, properties" },
          { name: "Organic Reactions", weeks: "3-5", description: "Substitution, elimination, hydrolysis" },
          { name: "Acyl Chlorides", weeks: "6-7", description: "Formation, reactions" },
          { name: "Fats and Oils", weeks: "8-9", description: "Triglycerides, saponification" },
          { name: "Carbohydrates", weeks: "10-12", description: "Mono-, di-, polysaccharides, structure" }
        ]
      }
    ]
  }
};

// Chemistry Scheme of Work Templates
export const chemistrySchemeOfWork = {
  "Year 11": {
    term1: [
      { week: 1, topic: "Atomic Structure", outcomes: "Sketch and label the structure of the atom", activities: "Atomic model drawing, electron identification" },
      { week: 2, topic: "Mass Number and Isotopes", outcomes: "Define mass number, isotope, atomic number", activities: "Isotope calculations, periodic table work" },
      { week: 3, topic: "Electronic Configuration", outcomes: "Describe the Octet rule", activities: "Electron shell diagrams, first 20 elements" },
      { week: 4, topic: "Writing Electron Configurations", outcomes: "Write electronic configuration of first 20 elements", activities: "Configuration practice, element identification" },
      { week: 5, topic: "Periodic Table Trends", outcomes: "Use Periodic Table to locate elements", activities: "Trend analysis, group/period work" },
      { week: 6, topic: "Lewis Structures", outcomes: "Draw Lewis structures of atoms", activities: "Lewis dot diagrams, valence electrons" },
      { week: 7, topic: "Ionic Bonding", outcomes: "Define electrovalent (ionic) bonding", activities: "Electron transfer diagrams, formula writing" },
      { week: 8, topic: "Covalent Bonding", outcomes: "Define covalent bonding", activities: "Electron sharing diagrams, molecule building" },
      { week: 9, topic: "Properties of Compounds", outcomes: "State properties of ionic and covalent compounds", activities: "Property comparison table, practical tests" },
      { week: 10, topic: "Chemical Equations", outcomes: "Write chemical formulas and balanced equations", activities: "Equation balancing practice" }
    ],
    term2: [
      { week: 1, topic: "The Mole Concept", outcomes: "Describe the quantity of matter and the mole concept", activities: "Mole calculations introduction" },
      { week: 2, topic: "Avogadro's Number", outcomes: "Define molar mass, mass, and Avogadro's number", activities: "Number calculations, particle counting" },
      { week: 3, topic: "Mole Calculations", outcomes: "Calculate moles using various formulas", activities: "Practice problems, real-world applications" },
      { week: 4, topic: "Percentage Composition", outcomes: "Calculate percentage composition", activities: "Composition calculations, compound analysis" },
      { week: 5, topic: "Empirical Formulas", outcomes: "Determine empirical/molecular formulas", activities: "Formula determination from data" },
      { week: 6, topic: "Stoichiometry Introduction", outcomes: "Define stoichiometry", activities: "Mole ratio calculations" },
      { week: 7, topic: "Water Structure", outcomes: "Draw structure of water and identify polarity", activities: "Molecular modeling, polarity experiments" },
      { week: 8, topic: "Solutions", outcomes: "Describe solubility and concentration", activities: "Solution preparation, concentration calculations" },
      { week: 9, topic: "Acids and Bases", outcomes: "Describe acidic and basic solutions using pH", activities: "pH testing, indicator experiments" },
      { week: 10, topic: "Quantitative Chemistry Practical", outcomes: "Demonstrate practical investigation procedures", activities: "Lab practical assessment" }
    ],
    term3: [
      { week: 1, topic: "Energy Conservation", outcomes: "Describe law of conservation of energy", activities: "Energy transformation examples" },
      { week: 2, topic: "Kinetic and Potential Energy", outcomes: "Define potential and kinetic energy", activities: "Energy identification activities" },
      { week: 3, topic: "Atmospheric Chemistry", outcomes: "Describe composition of atmosphere", activities: "Greenhouse gas research" },
      { week: 4, topic: "Combustion Reactions", outcomes: "Write word equations for combustion", activities: "Combustion demonstrations" },
      { week: 5, topic: "Collision Theory", outcomes: "Describe Collision Theory", activities: "Rate factor experiments" },
      { week: 6, topic: "Reaction Rates", outcomes: "Explain effect of temperature on rates", activities: "Rate investigation practical" },
      { week: 7, topic: "Chemical Equilibrium", outcomes: "Define dynamic equilibrium", activities: "Equilibrium demonstrations" },
      { week: 8, topic: "Redox Introduction", outcomes: "Define oxidation and reduction", activities: "Redox identification exercises" },
      { week: 9, topic: "Organic Chemistry Introduction", outcomes: "Define hydrocarbons", activities: "Hydrocarbon models, naming practice" },
      { week: 10, topic: "Revision and Assessment", outcomes: "Review all topics", activities: "Practice exams, revision sessions" }
    ]
  },
  "Year 12": {
    term1: [
      { week: 1, topic: "Ionic Compounds Review", outcomes: "Write formulas for ions and compounds", activities: "Formula writing practice" },
      { week: 2, topic: "Periodic Trends", outcomes: "Determine trends in ionization energy", activities: "Trend graphing, data analysis" },
      { week: 3, topic: "Isotope Calculations", outcomes: "Calculate % abundance and atomic mass", activities: "Mass spectrometry data analysis" },
      { week: 4, topic: "Intermolecular Forces", outcomes: "Explain intermolecular forces", activities: "Force comparison, boiling point trends" },
      { week: 5, topic: "Oxide Properties", outcomes: "Describe trends in oxide properties", activities: "Oxide reactions, equation writing" },
      { week: 6, topic: "Complex Ions", outcomes: "Name complex ions and describe colors", activities: "Complex ion formation, color identification" },
      { week: 7, topic: "Advanced Stoichiometry", outcomes: "Determine limiting reactants", activities: "Limiting reactant calculations" },
      { week: 8, topic: "Percentage Yield", outcomes: "Calculate percentage yield", activities: "Yield calculations, practical application" },
      { week: 9, topic: "Standard Solutions", outcomes: "Prepare standard solutions", activities: "Solution preparation practical" },
      { week: 10, topic: "Assessment", outcomes: "Demonstrate understanding", activities: "Term test, practical assessment" }
    ],
    term2: [
      { week: 1, topic: "Acid-Base Titrations", outcomes: "Explain acid-base titrations", activities: "Titration practical, calculations" },
      { week: 2, topic: "Titration Calculations", outcomes: "Calculate unknown concentrations", activities: "Calculation practice, error analysis" },
      { week: 3, topic: "Acid-Base Models", outcomes: "Define acid and base using different models", activities: "Model comparison, conjugate pairs" },
      { week: 4, topic: "pH Calculations", outcomes: "Calculate pH and pOH", activities: "pH calculation practice" },
      { week: 5, topic: "Thermochemistry", outcomes: "Define enthalpy of reaction", activities: "Energy diagram drawing" },
      { week: 6, topic: "Hess's Law", outcomes: "Apply Hess's Law", activities: "Enthalpy calculations" },
      { week: 7, topic: "Reaction Rates", outcomes: "List factors affecting rates", activities: "Rate investigations" },
      { week: 8, topic: "Le Chatelier's Principle", outcomes: "Apply Le Chatelier's Principle", activities: "Equilibrium shift predictions" },
      { week: 9, topic: "Industrial Processes", outcomes: "Explain Contact and Haber processes", activities: "Industrial chemistry research" },
      { week: 10, topic: "Assessment", outcomes: "Demonstrate understanding", activities: "Term test" }
    ],
    term3: [
      { week: 1, topic: "Oxidation States", outcomes: "Determine oxidation states", activities: "Oxidation state practice" },
      { week: 2, topic: "Balancing Redox", outcomes: "Balance using ion-electron method", activities: "Half-reaction balancing" },
      { week: 3, topic: "Electrolysis", outcomes: "Describe electrolysis process", activities: "Electrolysis practical" },
      { week: 4, topic: "Alcohols", outcomes: "Identify alcohol types and reactions", activities: "Alcohol oxidation practical" },
      { week: 5, topic: "Aldehydes and Ketones", outcomes: "Carry out tests for aldehydes/ketones", activities: "Chemical tests practical" },
      { week: 6, topic: "Carboxylic Acids", outcomes: "Describe acidic behavior", activities: "Acid reactions practical" },
      { week: 7, topic: "Esters", outcomes: "Write esterification equations", activities: "Ester preparation practical" },
      { week: 8, topic: "Fats and Oils", outcomes: "Describe fats and oils structure", activities: "Soap making practical" },
      { week: 9, topic: "Revision", outcomes: "Review all topics", activities: "Practice exams" },
      { week: 10, topic: "Final Assessment", outcomes: "Demonstrate understanding", activities: "Final examination" }
    ]
  },
  "Year 13": {
    term1: [
      { week: 1, topic: "Atomic Spectra", outcomes: "Describe atomic spectra", activities: "Spectroscopy demonstration" },
      { week: 2, topic: "Bohr Model", outcomes: "Describe Bohr model of hydrogen", activities: "Energy level calculations" },
      { week: 3, topic: "Quantum Numbers", outcomes: "Relate wave mechanics to electrons", activities: "Orbital diagrams" },
      { week: 4, topic: "Electron Configurations", outcomes: "Write ground state configurations", activities: "Configuration practice" },
      { week: 5, topic: "VSEPR Theory", outcomes: "Discuss molecular shapes", activities: "Molecular modeling" },
      { week: 6, topic: "Polarity", outcomes: "Determine polarity of molecules", activities: "Polarity prediction" },
      { week: 7, topic: "Nuclear Chemistry", outcomes: "Define radioactivity types", activities: "Radiation identification" },
      { week: 8, topic: "Half-life Calculations", outcomes: "Perform half-life calculations", activities: "Decay calculations" },
      { week: 9, topic: "Transition Metals", outcomes: "Describe transition metal properties", activities: "Complex ion formation" },
      { week: 10, topic: "Assessment", outcomes: "Demonstrate understanding", activities: "Term test" }
    ],
    term2: [
      { week: 1, topic: "Acid Dissociation Constants", outcomes: "Define Ka and relate to acid strength", activities: "Ka calculations" },
      { week: 2, topic: "pH from Ka", outcomes: "Calculate pH from Ka values", activities: "Advanced pH calculations" },
      { week: 3, topic: "Buffer Solutions", outcomes: "Define and describe buffer action", activities: "Buffer preparation" },
      { week: 4, topic: "Buffer Calculations", outcomes: "Calculate buffer pH", activities: "Henderson-Hasselbalch equation" },
      { week: 5, topic: "Titration Curves", outcomes: "Draw and interpret titration curves", activities: "Curve analysis" },
      { week: 6, topic: "Thermochemistry", outcomes: "Calculate enthalpy using bond energies", activities: "Bond energy calculations" },
      { week: 7, topic: "Equilibrium Constants", outcomes: "Calculate Kc values", activities: "Kc calculations" },
      { week: 8, topic: "Solubility Product", outcomes: "Calculate Ksp values", activities: "Solubility calculations" },
      { week: 9, topic: "Common Ion Effect", outcomes: "Determine common ion effect", activities: "Precipitation predictions" },
      { week: 10, topic: "Assessment", outcomes: "Demonstrate understanding", activities: "Term test" }
    ],
    term3: [
      { week: 1, topic: "Galvanic Cells", outcomes: "Describe galvanic cell components", activities: "Cell construction" },
      { week: 2, topic: "Electrode Potentials", outcomes: "Calculate cell potentials", activities: "E° calculations" },
      { week: 3, topic: "Redox Titrations", outcomes: "Perform redox titrations", activities: "Titration practical" },
      { week: 4, topic: "Organic Reactions", outcomes: "Describe organic reaction types", activities: "Reaction mechanism study" },
      { week: 5, topic: "Amines and Amides", outcomes: "Identify amine types", activities: "Organic synthesis" },
      { week: 6, topic: "Carbohydrates", outcomes: "Describe carbohydrate structure", activities: "Sugar testing" },
      { week: 7, topic: "Revision Week 1", outcomes: "Review atomic structure and bonding", activities: "Practice problems" },
      { week: 8, topic: "Revision Week 2", outcomes: "Review quantitative chemistry", activities: "Past papers" },
      { week: 9, topic: "Revision Week 3", outcomes: "Review all topics", activities: "Mock examination" },
      { week: 10, topic: "Final Examination", outcomes: "Demonstrate comprehensive understanding", activities: "External examination" }
    ]
  }
};

// Chemistry Lesson Plan Templates
export const chemistryLessonPlans = {
  "Year 11": [
    {
      id: "chem-y11-lp-1",
      week: 1,
      topic: "Structure of the Atom",
      duration: "80 minutes (double period)",
      objectives: [
        "Sketch and label the structure of the atom",
        "Identify protons, neutrons, and electrons",
        "State the charge and mass of subatomic particles"
      ],
      materials: ["Periodic table", "Atomic model kits", "Worksheets", "Whiteboard"],
      introduction: {
        time: "10 min",
        activity: "Discuss: What is everything made of? Show images of atoms. Introduce the concept of atomic structure."
      },
      development: [
        { time: "20 min", activity: "Direct teaching: Explain structure of atom - nucleus (protons, neutrons), electron shells. Draw labeled diagram together." },
        { time: "20 min", activity: "Model building: Students build atomic models using kits for hydrogen, helium, carbon." },
        { time: "20 min", activity: "Practice: Complete worksheet identifying parts of atoms, calculating number of each particle." }
      ],
      conclusion: {
        time: "10 min",
        activity: "Quick quiz: Label blank atom diagram. Discuss: Why is understanding atoms important for chemistry?"
      },
      assessment: "Can students correctly sketch and label an atom with all subatomic particles?",
      homework: "Draw and label atoms for first 10 elements using periodic table"
    },
    {
      id: "chem-y11-lp-2",
      week: 3,
      topic: "Electronic Configuration and Octet Rule",
      duration: "80 minutes",
      objectives: [
        "Describe the Octet rule",
        "Write electronic configurations for first 20 elements",
        "Explain why atoms form bonds"
      ],
      materials: ["Periodic table", "Electron shell diagrams", "Practice worksheets"],
      introduction: {
        time: "10 min",
        activity: "Review atomic structure. Introduce: How do electrons arrange themselves? Why is 8 special?"
      },
      development: [
        { time: "25 min", activity: "Teaching: Explain electron shells (2, 8, 8 pattern), Octet rule. Demonstrate with sodium and chlorine." },
        { time: "25 min", activity: "Guided practice: Work through configurations for elements 1-10 together, then 11-20." },
        { time: "10 min", activity: "Application: Predict which elements are stable/reactive based on electron configuration." }
      ],
      conclusion: {
        time: "10 min",
        activity: "Exit ticket: Write configurations for Na, Mg, Cl, Ar. Discuss: Why do atoms want full outer shells?"
      },
      assessment: "Can students correctly write electronic configurations and explain the Octet rule?",
      homework: "Complete configuration table for elements 1-20"
    }
  ],
  "Year 12": [
    {
      id: "chem-y12-lp-1",
      week: 1,
      topic: "Acid-Base Titrations",
      duration: "80 minutes",
      objectives: [
        "Explain the purpose of acid-base titrations",
        "Set up and perform a titration",
        "Calculate unknown concentrations from titration data"
      ],
      materials: ["Burette", "Conical flask", "Standard solution", "Indicator", "Safety equipment"],
      introduction: {
        time: "10 min",
        activity: "Review: What are acids and bases? Introduce titration as a quantitative technique."
      },
      development: [
        { time: "20 min", activity: "Demonstration: Teacher demonstrates proper titration technique, safety procedures." },
        { time: "35 min", activity: "Practical: Students perform titration of HCl with NaOH. Record results accurately." },
        { time: "10 min", activity: "Calculation: Use titration data to calculate unknown concentration." }
      ],
      conclusion: {
        time: "5 min",
        activity: "Discussion: Sources of error, how to improve accuracy. Clean up procedures."
      },
      assessment: "Can students perform accurate titrations and calculate concentrations?",
      homework: "Complete titration report and calculation questions"
    }
  ],
  "Year 13": [
    {
      id: "chem-y13-lp-1",
      week: 3,
      topic: "Galvanic Cells",
      duration: "80 minutes",
      objectives: [
        "Describe components of a galvanic cell",
        "Explain how galvanic cells produce electricity",
        "Write half-equations for electrode reactions"
      ],
      materials: ["Galvanic cell kit", "Voltmeter", "Metal electrodes", "Salt bridge materials"],
      introduction: {
        time: "10 min",
        activity: "Discuss: How do batteries work? Show examples of everyday electrochemical cells."
      },
      development: [
        { time: "20 min", activity: "Teaching: Explain galvanic cell components - anode, cathode, salt bridge, external circuit." },
        { time: "30 min", activity: "Practical: Build galvanic cell using Zn and Cu electrodes. Measure voltage." },
        { time: "15 min", activity: "Analysis: Write half-equations for anode and cathode. Explain electron flow." }
      ],
      conclusion: {
        time: "5 min",
        activity: "Summary: Key features of galvanic cells. Preview: Calculating cell potentials next lesson."
      },
      assessment: "Can students construct a galvanic cell and explain how it works?",
      homework: "Draw and label galvanic cell diagram, write half-equations"
    }
  ]
};

export default chemistryCurriculum;
